import React from "react";
import { Heart, Thermometer, Activity, Wind, Gauge, Brain } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface News2VitalEntry {
  vitalType: string;
  label: string;
  value: string;
  unit: string;
}

interface NEWS2VitalsCardProps {
  isDarkMode: boolean;
  news2Score: number;
  riskLabel: string;
  vitals: News2VitalEntry[];
}

const getIcon = (type: string, cls: string) => {
  switch (type) {
    case "respiratory-rate":
      return <Wind className={cls} />;
    case "heart-rate":
      return <Heart className={cls} />;
    case "temperature":
      return <Thermometer className={cls} />;
    case "oxygen-saturation":
      return <Gauge className={cls} />;
    case "consciousness-level":
      return <Brain className={cls} />;
    case "supplemental-oxygen":
      return <Wind className={cls} />;
    default:
      return <Activity className={cls} />;
  }
};

const riskBadgeCls = (score: number, isDark: boolean) => {
  if (score >= 7)
    return isDark
      ? "bg-red-900/40 text-red-300 border-red-700"
      : "bg-red-100 text-red-800 border-red-200";
  if (score >= 5)
    return isDark
      ? "bg-amber-900/40 text-amber-300 border-amber-700"
      : "bg-amber-100 text-amber-800 border-amber-200";
  return isDark
    ? "bg-green-900/40 text-green-300 border-green-700"
    : "bg-green-100 text-green-800 border-green-200";
};

export const NEWS2VitalsCard: React.FC<NEWS2VitalsCardProps> = ({
  isDarkMode,
  news2Score,
  riskLabel,
  vitals,
}) => {
  const iconCls = `w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;

  return (
    <div className="px-3 pb-3">
      {/* Score + risk row */}
      <div className="flex items-center gap-2 mt-2 mb-3 flex-wrap">
        <Badge
          className={`text-xs border font-semibold ${riskBadgeCls(news2Score, isDarkMode)}`}
        >
          NEWS2 Score: {news2Score}
        </Badge>
        {riskLabel && riskLabel !== "Insufficient data" && (
          <span
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {riskLabel}
          </span>
        )}
      </div>

      {/* Vital chips — read-only, no click/delete */}
      <div className="flex flex-wrap gap-2">
        {vitals.map((v, i) => (
          <div
            key={i}
            className={`flex flex-col items-center p-2.5 rounded-xl flex-1 basis-[calc(33.333%-6px)] max-w-[calc(33.333%-6px)] ${
              isDarkMode ? "bg-gray-800/50" : "bg-white/80"
            }`}
          >
            {getIcon(v.vitalType, iconCls)}
            <p
              className={`text-base font-bold mt-1 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
            >
              {v.value}
              {v.unit && (
                <span
                  className={`text-xs font-normal ml-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {v.unit}
                </span>
              )}
            </p>
            <p
              className={`text-xs text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              {v.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};
